import React, { useState, useEffect } from 'react';
import { API_URL, ADMIN_TOKEN_KEY } from './shared';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  'X-Admin-Token': sessionStorage.getItem(ADMIN_TOKEN_KEY) || '',
});

export const FilterGroupsModal = ({ products, onClose }) => {
  const [groups, setGroups] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/filter-groups`).then(r => r.json()).then(setGroups).catch(() => setGroups([]));
  }, []);

  const setGroup = (gi, patch) =>
    setGroups(prev => prev.map((g, i) => (i === gi ? { ...g, ...patch } : g)));

  const toggleProduct = (gi, pid) =>
    setGroups(prev => prev.map((g, i) => {
      if (i !== gi) return g;
      const ids = g.product_ids || [];
      return { ...g, product_ids: ids.includes(pid) ? ids.filter(x => x !== pid) : [...ids, pid] };
    }));

  const moveGroup = (gi, dir) =>
    setGroups(prev => {
      const to = gi + dir;
      if (to < 0 || to >= prev.length) return prev;
      const arr = [...prev];
      [arr[gi], arr[to]] = [arr[to], arr[gi]];
      return arr;
    });

  const addGroup = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/filter-groups`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ label: { es: 'Nuevo grupo', en: 'New group' }, product_ids: [] }),
      });
      if (res.ok) {
        const created = await res.json();
        setGroups(prev => [...prev, created]);
        window.dispatchEvent(new Event('productsUpdated'));
      }
    } catch { alert('No se pudo crear el grupo'); }
  };

  const deleteGroup = async (g) => {
    if (!window.confirm(`¿Eliminar el grupo "${g.label?.es}"?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/filter-groups/${g.group_id}`, {
        method: 'DELETE', headers: authHeaders(),
      });
      if (res.ok) {
        setGroups(prev => prev.filter(x => x.group_id !== g.group_id));
        window.dispatchEvent(new Event('productsUpdated'));
      }
      else alert('No se pudo eliminar');
    } catch { alert('No se pudo conectar'); }
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      for (let i = 0; i < groups.length; i++) {
        const g = groups[i];
        await fetch(`${API_URL}/api/admin/filter-groups/${g.group_id}`, {
          method: 'PUT', headers: authHeaders(),
          body: JSON.stringify({ label: g.label, product_ids: g.product_ids || [], order: i + 1 }),
        });
      }
      window.dispatchEvent(new Event('productsUpdated'));
      onClose(true);
    } catch { alert('Error al guardar'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" onClick={() => onClose(false)} data-testid="filter-groups-modal">
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h4 className="font-bold text-slate-800 text-sm">Filtros del catálogo</h4>
            <p className="text-[10px] text-slate-400">Edita los botones Lana/Algodón/… : nombre, productos que muestran, orden. "Todos" siempre aparece.</p>
          </div>
          <button onClick={() => onClose(false)} className="text-slate-400 hover:text-slate-700 text-lg" data-testid="close-filter-groups">✕</button>
        </div>
        <div className="p-4 overflow-y-auto space-y-3">
          {groups === null && <p className="text-center text-slate-400 text-sm py-6">Cargando…</p>}
          {(groups || []).map((g, gi) => (
            <div key={g.group_id} className="border border-slate-200 rounded-lg p-3" data-testid={`filter-group-${g.group_id}`}>
              <div className="flex items-center gap-2 mb-2">
                <div className="flex flex-col">
                  <button onClick={() => moveGroup(gi, -1)} disabled={gi === 0} className="text-slate-400 hover:text-slate-700 disabled:opacity-25 text-[10px] leading-none">▲</button>
                  <button onClick={() => moveGroup(gi, 1)} disabled={gi === groups.length - 1} className="text-slate-400 hover:text-slate-700 disabled:opacity-25 text-[10px] leading-none">▼</button>
                </div>
                <input type="text" value={g.label?.es || ''}
                  onChange={(e) => setGroup(gi, { label: { ...g.label, es: e.target.value } })}
                  placeholder="Nombre (español)"
                  className="flex-1 px-2 py-1 border border-slate-200 rounded text-sm font-semibold focus:outline-none focus:ring-1 focus:ring-cyan-500"
                  data-testid={`group-label-es-${g.group_id}`} />
                <input type="text" value={g.label?.en || ''}
                  onChange={(e) => setGroup(gi, { label: { ...g.label, en: e.target.value } })}
                  placeholder="Nombre (inglés)"
                  className="flex-1 px-2 py-1 border border-slate-200 rounded text-sm text-slate-600 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                  data-testid={`group-label-en-${g.group_id}`} />
                <button onClick={() => deleteGroup(g)} className="text-red-400 hover:text-red-600 text-sm font-bold px-1" title="Eliminar grupo" data-testid={`delete-group-${g.group_id}`}>🗑</button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-2 gap-y-1">
                {products.map(p => (
                  <label key={p.product_id} className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer">
                    <input type="checkbox"
                      checked={(g.product_ids || []).includes(p.product_id)}
                      onChange={() => toggleProduct(gi, p.product_id)}
                      className="accent-cyan-600"
                      data-testid={`group-${g.group_id}-product-${p.product_id}`} />
                    <span className="truncate">{p.name?.es}</span>
                  </label>
                ))}
              </div>
              <p className="text-[10px] text-slate-400 mt-1">{(g.product_ids || []).length} productos en este grupo</p>
            </div>
          ))}
          <button onClick={addGroup}
            className="w-full py-2 border-2 border-dashed border-slate-300 rounded-lg text-xs text-slate-500 hover:border-cyan-400 hover:text-cyan-700 font-semibold"
            data-testid="add-filter-group">+ Añadir grupo</button>
        </div>
        <div className="px-4 py-2.5 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={() => onClose(false)} className="px-4 py-1.5 text-xs text-slate-500 hover:text-slate-700 font-semibold">Cancelar</button>
          <button onClick={saveAll} disabled={saving || !groups}
            className="px-4 py-1.5 bg-cyan-600 text-white text-xs font-semibold rounded-lg hover:bg-cyan-700 disabled:opacity-60"
            data-testid="save-filter-groups">{saving ? 'Guardando…' : 'Guardar cambios'}</button>
        </div>
      </div>
    </div>
  );
};
